import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, X, ExternalLink, Target, Users, ChevronRight, ArrowRight, Share2, Twitter, Facebook, Link2, Music } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const GOFUNDME_URL = 'https://www.gofundme.com/f/help-open-the-brownstone-jazz-club';

// Custom Heart with Brownstone Logo (Reversed Treble Clef 𝄞) in black
// clefSize: 'sm' | 'md' | 'lg' controls the treble clef size
export const MusicHeart = ({ className = "w-7 h-7", animated = false, clefSize = "md" }) => {
  const sizeStyles = {
    sm: { fontSize: '45%', translateY: '-8%' },
    md: { fontSize: '60%', translateY: '-5%' },
    lg: { fontSize: '85%', translateY: '-3%' },
  };
  
  const style = sizeStyles[clefSize] || sizeStyles.md;
  
  return (
    <div className={`relative ${className} ${animated ? 'animate-pulse' : ''}`}>
      {/* Heart shape */}
      <svg viewBox="0 0 24 24" className="w-full h-full" fill="currentColor">
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
      </svg>
      {/* Reversed Treble Clef - Brownstone Logo */}
      <span 
        className="absolute inset-0 flex items-center justify-center text-black font-bold"
        style={{ 
          fontSize: style.fontSize, 
          transform: `scaleX(-1) translateY(${style.translateY})`,
          fontFamily: 'serif'
        }}
      >
        𝄞
      </span>
    </div>
  );
};

// Sequin Glitter Effect Component - Creates many sparkles like shimmering sequins
export const SequinGlitter = ({ count = 25, className = "" }) => {
  const sequins = Array.from({ length: count }, (_, i) => {
    const size = ['sequin-sm', '', 'sequin-lg'][Math.floor(Math.random() * 3)];
    const delay = (Math.random() * 3).toFixed(2);
    const duration = (1 + Math.random() * 2).toFixed(2);
    const left = Math.random() * 100;
    const top = Math.random() * 100;
    
    return (
      <div
        key={i}
        className={`sequin ${size}`}
        style={{
          left: `${left}%`,
          top: `${top}%`,
          animationDelay: `${delay}s`,
          animationDuration: `${duration}s`,
        }}
      />
    );
  });
  
  return (
    <div className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`}>
      {sequins}
    </div>
  );
};

// Share Modal Component
const ShareModal = ({ isOpen, onClose, url, title }) => {
  if (!isOpen) return null;
  
  const shareText = `Help open The Brownstone Jazz Club in Uptown Minneapolis! 🎷🎵 ${title}`;
  
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  };
  
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };
  
  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-sm bg-obsidian-light border border-white/10 rounded-2xl p-6 shadow-2xl">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white">
          <X className="w-5 h-5" />
        </button>
        
        <h3 className="font-display text-xl text-white mb-2">Share This Campaign</h3>
        <p className="text-white/50 text-sm mb-6">Help spread the word and support our dream!</p>
        
        <div className="flex gap-3 mb-4">
          <a 
            href={shareLinks.twitter}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white py-3 rounded-lg font-medium transition-colors"
          >
            <Twitter className="w-5 h-5" />
            Twitter
          </a>
          <a 
            href={shareLinks.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 bg-[#4267B2] hover:bg-[#365899] text-white py-3 rounded-lg font-medium transition-colors"
          >
            <Facebook className="w-5 h-5" />
            Facebook
          </a>
        </div>
        
        <button 
          onClick={copyToClipboard}
          className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-medium transition-colors"
        >
          <Link2 className="w-5 h-5" />
          Copy Link
        </button>
      </div>
    </>
  );
};

// Hook to fetch campaign data
const useCampaignData = () => {
  const [data, setData] = useState({
    raised: 636,
    goal: 16500,
    donors: 6,
    lastUpdated: null
  });
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/gofundme/stats`);
        if (response.data) {
          setData({
            raised: response.data.raised_amount,
            goal: response.data.goal_amount,
            donors: response.data.donor_count,
            lastUpdated: response.data.last_updated
          });
        }
      } catch (error) {
        // Use defaults if API fails
        console.log('Using default campaign data');
      }
    };
    
    fetchData();
    // Refresh every hour
    const interval = setInterval(fetchData, 3600000);
    return () => clearInterval(interval);
  }, []);
  
  return data;
};

// Header Banner Component
export const GoFundMeBanner = () => {
  const [dismissed, setDismissed] = useState(false);
  const campaign = useCampaignData();
  
  if (dismissed) return null;
  
  return (
    <div className="bg-gradient-to-r from-rose-600 via-pink-600 to-rose-600 text-white relative overflow-hidden">
      {/* Subtle shimmer only - no sequins on thin banner */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-shimmer-slow" />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-2 sm:py-3 relative">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <MusicHeart className="w-5 h-5 text-white" animated />
            <p className="text-sm sm:text-base font-medium truncate">
              <span className="hidden sm:inline">Help us open The Brownstone Jazz Club! </span>
              <span className="sm:hidden">Support our dream! </span>
              <span className="text-white/80">${campaign.raised.toLocaleString()} raised of ${(campaign.goal/1000).toFixed(1)}K goal</span>
            </p>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            <a 
              href={GOFUNDME_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 bg-white text-rose-600 hover:bg-white/90 px-3 sm:px-4 py-1.5 rounded-full text-sm font-semibold transition-all hover:scale-105"
              data-testid="gofundme-banner-btn"
            >
              <span>Donate</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button 
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Condensed Banner for Homepage - Links directly to GoFundMe with shimmer effect
export const GoFundMeHomeBanner = () => {
  const campaign = useCampaignData();
  const progressPercent = Math.min((campaign.raised / campaign.goal) * 100, 100);
  const [showShare, setShowShare] = useState(false);
  
  return (
    <>
      <div 
        className="block group relative overflow-hidden"
        data-testid="gofundme-home-banner"
      >
        {/* Sequin glitter effect - many sparkles */}
        <SequinGlitter count={35} className="rounded-2xl" />
        
        {/* Shimmer overlay */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-shimmer-slow" />
        </div>
        
        <div className="bg-gradient-to-r from-rose-600/20 via-pink-600/20 to-rose-600/20 border border-rose-500/30 rounded-2xl p-6 hover:border-rose-500/50 transition-all hover:bg-rose-600/25 relative">
          <a 
            href={GOFUNDME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          >
            {/* Left side */}
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform relative">
                <MusicHeart className="w-8 h-8 text-white" clefSize="lg" />
                {/* Pulse ring */}
                <div className="absolute inset-0 rounded-full bg-rose-400/30 animate-ping-slow" />
              </div>
              <div>
                <h3 className="font-display text-xl text-white group-hover:text-rose-300 transition-colors">
                  Help Open The Brownstone
                </h3>
                <p className="text-white/60 text-sm">Support our GoFundMe campaign</p>
              </div>
            </div>
            
            {/* Progress */}
            <div className="flex-1 max-w-xs">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-white font-display text-lg">${campaign.raised.toLocaleString()}</span>
                <span className="text-white/50 text-sm">of ${(campaign.goal/1000).toFixed(1)}K</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-rose-500 to-pink-500 rounded-full relative"
                  style={{ width: `${progressPercent}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                </div>
              </div>
              <div className="flex items-center justify-between mt-1 text-xs">
                <span className="text-white/50">{campaign.donors} donors</span>
                <span className="text-rose-400">{progressPercent.toFixed(0)}%</span>
              </div>
            </div>
            
            {/* CTA */}
            <div className="flex items-center gap-2 text-rose-400 group-hover:text-rose-300 font-medium">
              <span>Donate Now</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </div>
          </a>
          
          {/* Share button */}
          <button 
            onClick={(e) => { e.preventDefault(); setShowShare(true); }}
            className="absolute top-3 right-3 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-all"
            title="Share this campaign"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      <ShareModal 
        isOpen={showShare}
        onClose={() => setShowShare(false)}
        url={GOFUNDME_URL}
        title="Help Open The Brownstone Jazz Club"
      />
    </>
  );
};

// Sidebar/Section Component
export const GoFundMeSection = ({ compact = false }) => {
  const campaign = useCampaignData();
  const progressPercent = Math.min((campaign.raised / campaign.goal) * 100, 100);
  const [showShare, setShowShare] = useState(false);
  
  if (compact) {
    return (
      <a 
        href={GOFUNDME_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="block bg-gradient-to-br from-rose-500/10 to-pink-500/10 border border-rose-500/30 rounded-xl p-4 hover:border-rose-500/50 transition-all group"
        data-testid="gofundme-compact"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center">
            <MusicHeart className="w-5 h-5 text-rose-400" />
          </div>
          <div>
            <p className="text-white font-medium text-sm">Support Our Dream</p>
            <p className="text-rose-400 text-xs">${campaign.raised} of ${(campaign.goal/1000).toFixed(1)}K raised</p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
          <div 
            className="h-full bg-gradient-to-r from-rose-500 to-pink-500 rounded-full transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        
        <div className="flex items-center justify-between text-xs">
          <span className="text-white/50">{campaign.donors} donors</span>
          <span className="text-rose-400 group-hover:text-rose-300 flex items-center gap-1">
            Contribute <ChevronRight className="w-3 h-3" />
          </span>
        </div>
      </a>
    );
  }
  
  return (
    <>
      <div className="bg-gradient-to-br from-obsidian-light to-obsidian border border-rose-500/20 rounded-2xl overflow-hidden relative" data-testid="gofundme-section">
        {/* Share button */}
        <button 
          onClick={() => setShowShare(true)}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-all"
          title="Share this campaign"
        >
          <Share2 className="w-4 h-4" />
        </button>
        
        {/* Header Image */}
        <div className="relative h-40 bg-gradient-to-br from-rose-600 to-pink-600 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.gofundme.com/a7LyM87vsCOq069FZdIG8zzJ91k=/1200x900/https://d2g8igdw686xgo.cloudfront.net/100825539_1773090427612741_r.png')] bg-cover bg-center opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian-light to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center gap-2 mb-1">
              <MusicHeart className="w-4 h-4 text-rose-400" clefSize="sm" />
              <span className="text-rose-300 text-sm font-medium">Community Fundraiser</span>
            </div>
            <h3 className="font-display text-xl text-white">Help Open The Brownstone Jazz Club</h3>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Progress */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <span className="text-2xl font-display text-white">${campaign.raised.toLocaleString()}</span>
                <span className="text-white/50 text-sm ml-1">raised</span>
              </div>
              <span className="text-white/50 text-sm">${campaign.goal.toLocaleString()} goal</span>
            </div>
            
            {/* Progress bar */}
            <div className="h-3 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-rose-500 via-pink-500 to-rose-400 rounded-full transition-all relative"
                style={{ width: `${progressPercent}%` }}
              >
                <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent)] animate-shimmer" />
              </div>
            </div>
            
            <div className="flex items-center justify-between mt-2 text-sm">
              <span className="text-white/50">{progressPercent.toFixed(0)}% of goal</span>
              <span className="text-white/50 flex items-center gap-1">
                <Users className="w-3.5 h-3.5" /> {campaign.donors} donors
              </span>
            </div>
          </div>
          
          {/* Description */}
          <p className="text-white/70 text-sm leading-relaxed">
            The Brownstone Jazz Club is on a mission to bring live jazz and vibrant community spirit back to Uptown Minneapolis. Your support helps cover permits, licensing, and a vibrant reopening.
          </p>
          
          {/* Why Support */}
          <div className="space-y-2">
            <p className="text-white/50 text-xs uppercase tracking-wide">Why Support Us</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white/5 rounded-lg p-2.5 text-center">
                <p className="text-rose-400 text-lg font-display">🎷</p>
                <p className="text-white/70 text-xs">Live Jazz Revival</p>
              </div>
              <div className="bg-white/5 rounded-lg p-2.5 text-center">
                <p className="text-rose-400 text-lg font-display">🤝</p>
                <p className="text-white/70 text-xs">Community Space</p>
              </div>
              <div className="bg-white/5 rounded-lg p-2.5 text-center">
                <p className="text-rose-400 text-lg font-display">🎨</p>
                <p className="text-white/70 text-xs">Support Artists</p>
              </div>
              <div className="bg-white/5 rounded-lg p-2.5 text-center">
                <p className="text-rose-400 text-lg font-display">🏘️</p>
                <p className="text-white/70 text-xs">Uptown Revival</p>
              </div>
            </div>
          </div>
          
          {/* CTA */}
          <a 
            href={GOFUNDME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <Button className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white py-5 font-medium gap-2 transition-all hover:scale-[1.02] justify-center">
              <MusicHeart className="w-4 h-4" clefSize="sm" />
              Contribute Now
              <ExternalLink className="w-4 h-4 ml-1" />
            </Button>
          </a>
          
          <p className="text-center text-white/40 text-xs">
            Organized by Dena Allen • Minneapolis, MN
          </p>
        </div>
      </div>
      
      <ShareModal 
        isOpen={showShare}
        onClose={() => setShowShare(false)}
        url={GOFUNDME_URL}
        title="Help Open The Brownstone Jazz Club"
      />
    </>
  );
};

export default GoFundMeSection;
